/**
 * ReconciliationWorkspace.tsx
 * The core APX bookkeeper workflow:
 *   Pull → AI Match → Allocate (Approve / Override / Skip) → Clear
 *   Anomalies auto-resolve as transactions are coded correctly.
 *
 * Layout: Two-panel split inside the hub shell
 *   LEFT  — Client list (filtered to assigned clients for bookkeeper, all for manager/admin)
 *           Each row: client name, practice badge, open tx count, anomaly count, health dot
 *   RIGHT — Active client transaction file
 *           Header: client name + period picker + Pull button + summary bar
 *           Body:   Needs Coding | Matched | Resolved | Anomalies tabs
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Sparkles,
  Clock,
  Search,
  ChevronRight,
  BarChart3,
  TrendingDown,
} from "lucide-react";

// ─── Palette (matches APX shell exactly) ─────────────────────────────────────
const S = {
  page:        "#F8F6F2",
  panel:       "#FFFFFF",
  panelBorder: "#D8D2C8",
  listHead:    "#EDE8E0",
  divider:     "#E8E3DC",
  rowHov:      "#F5F2EE",
  rowSel:      "#EDE8E0",
  text1:       "oklch(0.12 0.04 240)",
  text2:       "oklch(0.28 0.04 240)",
  textMuted:   "oklch(0.50 0.03 240)",
  gold:        "oklch(0.62 0.14 75)",
  goldBg:      "oklch(0.94 0.06 85)",
  goldBorder:  "oklch(0.80 0.10 75)",
  green:       "oklch(0.45 0.15 155)",
  greenBg:     "oklch(0.94 0.06 155)",
  amber:       "oklch(0.52 0.14 75)",
  amberBg:     "oklch(0.96 0.06 85)",
  red:         "oklch(0.50 0.20 25)",
  redBg:       "oklch(0.96 0.05 25)",
  blue:        "oklch(0.45 0.15 240)",
  blueBg:      "oklch(0.94 0.06 240)",
  shadow:      "0 1px 4px oklch(0.12 0.04 240 / 6%)",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
function fmtAmt(cents: number) {
  const abs = Math.abs(cents) / 100;
  const sign = cents < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}
function practiceStyle(key: string): React.CSSProperties {
  if (key === "apx")     return { background: S.goldBg,  color: S.gold,  border: `1px solid ${S.goldBorder}`, fontWeight: 700, fontSize: 10, padding: "1px 6px", borderRadius: 4 };
  if (key === "apx-hrb") return { background: S.blueBg,  color: S.blue,  border: `1px solid oklch(0.72 0.12 240)`, fontWeight: 700, fontSize: 10, padding: "1px 6px", borderRadius: 4 };
  if (key === "apx-bbn") return { background: S.greenBg, color: S.green, border: `1px solid oklch(0.75 0.12 155)`, fontWeight: 700, fontSize: 10, padding: "1px 6px", borderRadius: 4 };
  return { background: "#eee", color: "#555", fontSize: 10, padding: "1px 6px", borderRadius: 4 };
}
function practiceLabel(key: string) {
  if (key === "apx") return "APX";
  if (key === "apx-hrb") return "HRB";
  if (key === "apx-bbn") return "BBN";
  return key.toUpperCase();
}
function healthDot(health: string) {
  const c = health === "green" ? S.green : health === "yellow" ? S.amber : health === "red" ? S.red : S.textMuted;
  return <span style={{ width: 8, height: 8, borderRadius: "50%", background: c, display: "inline-block", flexShrink: 0 }} />;
}
function confidenceStyle(n: number): React.CSSProperties {
  if (n >= 80) return { background: S.greenBg, color: S.green, border: `1px solid oklch(0.75 0.12 155)` };
  if (n >= 60) return { background: S.amberBg, color: S.amber, border: `1px solid oklch(0.80 0.12 85)` };
  return { background: S.redBg, color: S.red, border: `1px solid oklch(0.75 0.16 25)` };
}

// ─── Override Dialog ──────────────────────────────────────────────────────────
interface OverrideDialogProps {
  open: boolean;
  onClose: () => void;
  tx: { id: number; coreClientId: number; contactName?: string | null; description?: string | null; amount: number; accountCode?: string | null; accountName?: string | null; aiSuggestedCode?: string | null; aiSuggestedName?: string | null };
  resolvedBy: string;
  resolvedByRole: string;
  onDone: () => void;
}
function OverrideDialog({ open, onClose, tx, resolvedBy, resolvedByRole, onDone }: OverrideDialogProps) {
  const [code, setCode] = useState(tx.aiSuggestedCode ?? tx.accountCode ?? "");
  const [name, setName] = useState(tx.aiSuggestedName ?? tx.accountName ?? "");
  const [notes, setNotes] = useState("");
  const resolve = trpc.transactionReview.resolve.useMutation({
    onSuccess: () => { toast.success("Override saved"); onDone(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle style={{ color: S.text1, fontFamily: "'Playfair Display', serif" }}>Override Account Code</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg p-3 text-sm space-y-1" style={{ background: S.listHead }}>
            <div className="font-semibold" style={{ color: S.text1 }}>{tx.contactName ?? "Unknown Vendor"}</div>
            <div style={{ color: S.textMuted }}>{tx.description ?? "No description"}</div>
            <div className="font-bold" style={{ color: tx.amount < 0 ? S.red : S.green }}>{fmtAmt(tx.amount)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Account Code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. 400" className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Account Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Advertising" className="h-8 text-sm" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for override..." className="text-sm h-20 resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!code || !name || resolve.isPending}
            style={{ background: S.gold, color: "#fff", border: "none" }}
            onClick={() => resolve.mutate({ transactionReviewId: tx.id, coreClientId: tx.coreClientId, action: "overridden", finalCode: code, finalName: name, notes: notes || undefined, resolvedBy, resolvedByRole })}>
            {resolve.isPending ? "Saving…" : "Save Override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Transaction Row ──────────────────────────────────────────────────────────
interface TxRowProps {
  tx: {
    id: number; coreClientId: number; txDate: string; description?: string | null;
    contactName?: string | null; amount: number; accountCode?: string | null;
    accountName?: string | null; matchStatus: string; aiSuggestedCode?: string | null;
    aiSuggestedName?: string | null; aiConfidence?: number | null; reviewStatus: string;
    reviewedBy?: string | null;
  };
  resolvedBy: string; resolvedByRole: string; onDone: () => void; showActions: boolean;
}
function TxRow({ tx, resolvedBy, resolvedByRole, onDone, showActions }: TxRowProps) {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const resolve = trpc.transactionReview.resolve.useMutation({
    onSuccess: () => { toast.success("Saved"); onDone(); },
    onError: (e) => toast.error(e.message),
  });
  const isPending = resolve.isPending;

  return (
    <>
      <div className="px-4 py-3 transition-colors"
        style={{ borderBottom: `1px solid ${S.divider}` }}
        onMouseEnter={(e) => { if (!overrideOpen) (e.currentTarget as HTMLElement).style.background = S.rowHov; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
        <div className="flex items-start gap-3">
          {/* Amount */}
          <div className="w-20 shrink-0 text-right">
            <span className="text-sm font-bold" style={{ color: tx.amount < 0 ? S.red : S.green }}>{fmtAmt(tx.amount)}</span>
          </div>
          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold truncate" style={{ color: S.text1 }}>{tx.contactName ?? "Unknown Vendor"}</span>
              <span className="text-xs" style={{ color: S.textMuted }}>{tx.txDate}</span>
              {tx.accountCode && (
                <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: S.listHead, color: S.text2 }}>
                  {tx.accountCode} — {tx.accountName}
                </span>
              )}
            </div>
            {tx.description && (
              <p className="text-xs mt-0.5 truncate" style={{ color: S.textMuted }}>{tx.description}</p>
            )}
            {/* AI suggestion */}
            {tx.aiSuggestedCode && showActions && tx.reviewStatus === "open" && (
              <div className="mt-2 flex items-center gap-2 rounded px-2.5 py-1.5 text-xs"
                style={{ ...confidenceStyle(tx.aiConfidence ?? 0), display: "inline-flex" }}>
                <Sparkles className="w-3 h-3 shrink-0" />
                <span>AI: <strong>{tx.aiSuggestedCode}</strong> — {tx.aiSuggestedName}</span>
                <span className="ml-1 font-bold">{tx.aiConfidence}%</span>
              </div>
            )}
            {/* Resolved info */}
            {tx.reviewStatus !== "open" && tx.reviewedBy && (
              <p className="text-xs mt-1" style={{ color: S.textMuted }}>
                Resolved by {tx.reviewedBy} · {tx.accountCode} — {tx.accountName}
              </p>
            )}
          </div>
          {/* Actions */}
          {showActions && tx.reviewStatus === "open" && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button disabled={isPending}
                className="text-xs font-semibold px-2.5 py-1 rounded transition-opacity hover:opacity-80"
                style={{ background: S.greenBg, color: S.green, border: `1px solid oklch(0.75 0.12 155)` }}
                onClick={() => resolve.mutate({ transactionReviewId: tx.id, coreClientId: tx.coreClientId, action: "approved", finalCode: tx.aiSuggestedCode ?? tx.accountCode ?? "", finalName: tx.aiSuggestedName ?? tx.accountName ?? "", resolvedBy, resolvedByRole })}>
                <CheckCircle2 className="w-3 h-3 inline mr-1" />Approve
              </button>
              <button disabled={isPending}
                className="text-xs font-semibold px-2.5 py-1 rounded transition-opacity hover:opacity-80"
                style={{ background: S.blueBg, color: S.blue, border: `1px solid oklch(0.72 0.12 240)` }}
                onClick={() => setOverrideOpen(true)}>
                Override
              </button>
              <button disabled={isPending}
                className="text-xs font-semibold px-2.5 py-1 rounded transition-opacity hover:opacity-80"
                style={{ background: S.listHead, color: S.textMuted, border: `1px solid ${S.divider}` }}
                onClick={() => resolve.mutate({ transactionReviewId: tx.id, coreClientId: tx.coreClientId, action: "skipped", finalCode: tx.accountCode ?? "", finalName: tx.accountName ?? "", resolvedBy, resolvedByRole })}>
                Skip
              </button>
            </div>
          )}
        </div>
      </div>
      {overrideOpen && (
        <OverrideDialog open={overrideOpen} onClose={() => setOverrideOpen(false)} tx={tx} resolvedBy={resolvedBy} resolvedByRole={resolvedByRole} onDone={onDone} />
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ReconciliationWorkspace() {
  const { user } = useAuth();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [period, setPeriod] = useState(currentPeriod());
  const [activeTab, setActiveTab] = useState("needs-coding");
  const [search, setSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");

  // Read ?clientId= from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = Number(params.get("clientId"));
    if (id > 0) { setSelectedClientId(id); setActiveTab("needs-coding"); }
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────
  const summariesQuery = trpc.analytics.getSummaries.useQuery({ period }, { staleTime: 60_000 });
  const summaries = summariesQuery.data ?? [];

  // Determine which clients to show based on role
  const myName = user?.name ?? "";
  const isManager = user?.role === "admin" || user?.role === "super_admin" || user?.role === "controller" || user?.role === "bookkeeping_manager" || user?.role === "bookkeeper_supervisor";

  const clientList = useMemo(() => {
    return summaries
      .filter((s: { t1Bookkeeper?: string | null; t2Bookkeeper?: string | null }) =>
        isManager ? true : (s.t1Bookkeeper === myName || s.t2Bookkeeper === myName)
      )
      .filter((s: { clientName: string }) =>
        clientSearch === "" || s.clientName.toLowerCase().includes(clientSearch.toLowerCase())
      );
  }, [summaries, isManager, myName, clientSearch]);

  const selectedClient = useMemo(() =>
    summaries.find((s: { clientId: number }) => s.clientId === selectedClientId) ?? null,
    [summaries, selectedClientId]
  );

  // Transaction data for selected client
  const reviewsQuery = trpc.transactionReview.getReviews.useQuery(
    { clientId: selectedClientId ?? 0, period },
    { enabled: !!selectedClientId, staleTime: 30_000 }
  );
  const summaryQuery = trpc.transactionReview.getSummary.useQuery(
    { clientId: selectedClientId ?? 0, period },
    { enabled: !!selectedClientId, staleTime: 30_000 }
  );
  const anomaliesQuery = trpc.analytics.getAllOpenAnomalies.useQuery(
    { period },
    { staleTime: 120_000, retry: false }
  );

  const pullMutation = trpc.transactionReview.pull.useMutation({
    onSuccess: (r) => {
      toast.success(`Pulled ${r.total} transactions — ${r.matched} matched, ${r.unmatched + r.newAccount} need review`);
      reviewsQuery.refetch();
      summaryQuery.refetch();
      summariesQuery.refetch();
    },
    onError: (e) => toast.error(`Pull failed: ${e.message}`),
  });

  const handleResolved = useCallback(() => {
    reviewsQuery.refetch();
    summaryQuery.refetch();
    summariesQuery.refetch();
  }, [reviewsQuery, summaryQuery, summariesQuery]);

  const reviews = reviewsQuery.data ?? [];
  const txSummary = summaryQuery.data;
  const resolvedBy = myName || "Staff";
  const resolvedByRole = user?.role ?? "bookkeeper";

  // Filter transactions by tab + search
  const filteredTx = useMemo(() => {
    let list = reviews;
    if (activeTab === "needs-coding") list = list.filter((t: { reviewStatus: string; matchStatus: string }) => t.reviewStatus === "open" && (t.matchStatus === "unmatched" || t.matchStatus === "new_account"));
    else if (activeTab === "matched")  list = list.filter((t: { matchStatus: string }) => t.matchStatus === "matched");
    else if (activeTab === "resolved") list = list.filter((t: { reviewStatus: string }) => t.reviewStatus !== "open");
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((t: { contactName?: string | null; description?: string | null; accountCode?: string | null }) =>
        (t.contactName ?? "").toLowerCase().includes(q) ||
        (t.description ?? "").toLowerCase().includes(q) ||
        (t.accountCode ?? "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [reviews, activeTab, search]);

  // Anomalies for selected client
  const clientAnomalies = useMemo(() => {
    if (!anomaliesQuery.data || !selectedClientId) return [];
    return anomaliesQuery.data.filter((a: { coreClientId: number }) => a.coreClientId === selectedClientId);
  }, [anomaliesQuery.data, selectedClientId]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full overflow-hidden" style={{ background: S.page }}>

      {/* ── LEFT: Client List ──────────────────────────────────────────── */}
      <div className="flex flex-col shrink-0 overflow-hidden" style={{ width: 280, borderRight: `1px solid ${S.panelBorder}`, background: S.panel }}>
        {/* Header */}
        <div className="px-4 py-3 shrink-0" style={{ background: S.listHead, borderBottom: `1px solid ${S.panelBorder}` }}>
          <h2 className="text-sm font-bold" style={{ fontFamily: "'Playfair Display', serif", color: S.text1 }}>
            {isManager ? "All Clients" : "My Clients"}
          </h2>
          <p className="text-xs mt-0.5" style={{ color: S.textMuted }}>
            {clientList.length} client{clientList.length !== 1 ? "s" : ""} · {period}
          </p>
        </div>
        {/* Search */}
        <div className="px-3 py-2 shrink-0" style={{ borderBottom: `1px solid ${S.divider}` }}>
          <div className="flex items-center gap-2 rounded px-2.5 py-1.5" style={{ background: S.listHead, border: `1px solid ${S.panelBorder}` }}>
            <Search className="w-3.5 h-3.5 shrink-0" style={{ color: S.textMuted }} />
            <input
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder="Search clients…"
              className="flex-1 bg-transparent text-xs outline-none"
              style={{ color: S.text1 }}
            />
          </div>
        </div>
        {/* Period picker */}
        <div className="px-3 py-2 shrink-0" style={{ borderBottom: `1px solid ${S.divider}` }}>
          <input type="month" value={period} onChange={(e) => { setPeriod(e.target.value); setSelectedClientId(null); }}
            className="w-full text-xs rounded px-2 py-1.5 outline-none"
            style={{ background: S.listHead, border: `1px solid ${S.panelBorder}`, color: S.text1 }} />
        </div>
        {/* Client rows */}
        <div className="flex-1 overflow-y-auto">
          {summariesQuery.isLoading ? (
            <div className="p-4 text-xs text-center" style={{ color: S.textMuted }}>Loading clients…</div>
          ) : clientList.length === 0 ? (
            <div className="p-4 text-xs text-center" style={{ color: S.textMuted }}>No clients found</div>
          ) : (
            clientList.map((c: {
              clientId: number; clientName: string; practiceKey: string;
              health: string; openAnomalies?: number; t1Bookkeeper?: string | null; t2Bookkeeper?: string | null
            }) => {
              const isSelected = c.clientId === selectedClientId;
              return (
                <button key={c.clientId} onClick={() => { setSelectedClientId(c.clientId); setActiveTab("needs-coding"); setSearch(""); }}
                  className="w-full text-left px-4 py-3 transition-colors"
                  style={{ background: isSelected ? S.rowSel : "transparent", borderBottom: `1px solid ${S.divider}`, borderLeft: isSelected ? `3px solid ${S.gold}` : "3px solid transparent" }}
                  onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = S.rowHov; }}
                  onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = "transparent"; }}>
                  <div className="flex items-center gap-2">
                    {healthDot(c.health)}
                    <span className="text-xs font-semibold truncate flex-1" style={{ color: S.text1 }}>{c.clientName}</span>
                    <span style={practiceStyle(c.practiceKey)}>{practiceLabel(c.practiceKey)}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 pl-4">
                    {(c.openAnomalies ?? 0) > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: S.redBg, color: S.red }}>
                        {c.openAnomalies} anomal{(c.openAnomalies ?? 0) === 1 ? "y" : "ies"}
                      </span>
                    )}
                    {isSelected && <ChevronRight className="w-3 h-3 ml-auto" style={{ color: S.gold }} />}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ── RIGHT: Transaction File ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selectedClientId ? (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{ color: S.textMuted }}>
            <BarChart3 className="w-12 h-12 opacity-30" />
            <p className="text-sm font-medium">Select a client to begin reconciliation</p>
            <p className="text-xs opacity-70">Choose from the list on the left</p>
          </div>
        ) : (
          <>
            {/* Client header */}
            <div className="shrink-0 px-6 py-4 flex items-center justify-between gap-4 flex-wrap"
              style={{ background: S.listHead, borderBottom: `1px solid ${S.panelBorder}`, boxShadow: S.shadow }}>
              <div>
                <div className="flex items-center gap-2">
                  {selectedClient && healthDot(selectedClient.health)}
                  <h2 className="text-base font-bold" style={{ fontFamily: "'Playfair Display', serif", color: S.text1 }}>
                    {selectedClient?.clientName ?? "Client"}
                  </h2>
                  {selectedClient && <span style={practiceStyle(selectedClient.practiceKey)}>{practiceLabel(selectedClient.practiceKey)}</span>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: S.textMuted }}>
                  T1: {selectedClient?.t1Bookkeeper ?? "—"} · T2: {selectedClient?.t2Bookkeeper ?? "—"}
                </p>
              </div>
              <Button
                size="sm"
                disabled={pullMutation.isPending}
                onClick={() => pullMutation.mutate({ clientId: selectedClientId, period })}
                style={{ background: S.gold, color: "#fff", border: "none", fontWeight: 600 }}>
                <RefreshCw className={`w-4 h-4 mr-1.5 ${pullMutation.isPending ? "animate-spin" : ""}`} />
                {pullMutation.isPending ? "Pulling…" : "Pull from Xero"}
              </Button>
            </div>

            {/* Summary bar */}
            {txSummary && (
              <div className="shrink-0 px-6 py-3 flex items-center gap-4 flex-wrap" style={{ borderBottom: `1px solid ${S.divider}`, background: S.panel }}>
                {[
                  { label: "Total",        value: txSummary.total,      color: S.text1 },
                  { label: "Needs Coding", value: txSummary.open,       color: S.red },
                  { label: "Matched",      value: txSummary.matched,    color: S.green },
                  { label: "New Account",  value: txSummary.newAccount, color: S.amber },
                  { label: "Resolved",     value: txSummary.total - txSummary.open, color: S.blue },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <span className="text-lg font-bold" style={{ color, fontFamily: "'Playfair Display', serif" }}>{value}</span>
                    <span className="text-xs" style={{ color: S.textMuted }}>{label}</span>
                    <span className="text-xs" style={{ color: S.divider, marginLeft: 4 }}>·</span>
                  </div>
                ))}
                {clientAnomalies.length > 0 && (
                  <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold"
                    style={{ background: S.redBg, color: S.red, border: `1px solid oklch(0.75 0.16 25)` }}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {clientAnomalies.length} open anomal{clientAnomalies.length === 1 ? "y" : "ies"}
                  </div>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <div className="shrink-0 px-6 pt-3 flex items-center justify-between gap-4 flex-wrap"
                  style={{ borderBottom: `1px solid ${S.divider}`, background: S.panel }}>
                  <TabsList className="h-8 bg-transparent gap-0 p-0 border-0">
                    {[
                      { value: "needs-coding", label: "Needs Coding", count: txSummary?.open },
                      { value: "matched",      label: "Matched",      count: txSummary?.matched },
                      { value: "resolved",     label: "Resolved",     count: undefined },
                      { value: "anomalies",    label: "Anomalies",    count: clientAnomalies.length || undefined },
                    ].map(({ value, label, count }) => (
                      <TabsTrigger key={value} value={value}
                        className="text-xs px-3 h-8 rounded-none border-b-2 data-[state=active]:border-b-2 data-[state=inactive]:border-transparent"
                        style={{ borderBottomColor: activeTab === value ? S.gold : "transparent", color: activeTab === value ? S.gold : S.textMuted, fontWeight: activeTab === value ? 700 : 400, background: "transparent" }}>
                        {label}
                        {count !== undefined && count > 0 && (
                          <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                            style={{ background: value === "needs-coding" ? S.red : value === "anomalies" ? S.red : S.green, color: "#fff" }}>
                            {count}
                          </span>
                        )}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="flex items-center gap-2 rounded px-2.5 py-1" style={{ background: S.listHead, border: `1px solid ${S.panelBorder}` }}>
                    <Search className="w-3.5 h-3.5 shrink-0" style={{ color: S.textMuted }} />
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search vendor, code…"
                      className="bg-transparent text-xs outline-none w-44"
                      style={{ color: S.text1 }} />
                  </div>
                </div>

                {/* Needs Coding */}
                <TabsContent value="needs-coding" className="flex-1 overflow-y-auto m-0 p-0" style={{ background: S.panel }}>
                  {reviewsQuery.isLoading ? (
                    <div className="p-8 text-center text-sm" style={{ color: S.textMuted }}>Loading transactions…</div>
                  ) : filteredTx.length === 0 ? (
                    <div className="p-12 flex flex-col items-center gap-3" style={{ color: S.textMuted }}>
                      <CheckCircle2 className="w-10 h-10" style={{ color: S.green, opacity: 0.7 }} />
                      <p className="text-sm font-medium" style={{ color: S.text2 }}>All transactions coded — client is clear!</p>
                      <p className="text-xs">Pull again to check for new transactions.</p>
                    </div>
                  ) : (
                    filteredTx.map((tx: Parameters<typeof TxRow>[0]["tx"]) => (
                      <TxRow key={tx.id} tx={tx} resolvedBy={resolvedBy} resolvedByRole={resolvedByRole} onDone={handleResolved} showActions={true} />
                    ))
                  )}
                </TabsContent>

                {/* Matched */}
                <TabsContent value="matched" className="flex-1 overflow-y-auto m-0 p-0" style={{ background: S.panel }}>
                  {filteredTx.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: S.textMuted }}>No matched transactions for this period.</div>
                  ) : (
                    filteredTx.map((tx: Parameters<typeof TxRow>[0]["tx"]) => (
                      <TxRow key={tx.id} tx={tx} resolvedBy={resolvedBy} resolvedByRole={resolvedByRole} onDone={handleResolved} showActions={false} />
                    ))
                  )}
                </TabsContent>

                {/* Resolved */}
                <TabsContent value="resolved" className="flex-1 overflow-y-auto m-0 p-0" style={{ background: S.panel }}>
                  {filteredTx.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: S.textMuted }}>No resolved transactions yet.</div>
                  ) : (
                    filteredTx.map((tx: Parameters<typeof TxRow>[0]["tx"]) => (
                      <TxRow key={tx.id} tx={tx} resolvedBy={resolvedBy} resolvedByRole={resolvedByRole} onDone={handleResolved} showActions={false} />
                    ))
                  )}
                </TabsContent>

                {/* Anomalies */}
                <TabsContent value="anomalies" className="flex-1 overflow-y-auto m-0 p-0" style={{ background: S.panel }}>
                  {clientAnomalies.length === 0 ? (
                    <div className="p-12 flex flex-col items-center gap-3">
                      <CheckCircle2 className="w-10 h-10" style={{ color: S.green, opacity: 0.7 }} />
                      <p className="text-sm font-medium" style={{ color: S.text2 }}>No open anomalies for this client</p>
                      <p className="text-xs" style={{ color: S.textMuted }}>Anomalies clear automatically as transactions are correctly coded.</p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: S.textMuted }}>
                        {clientAnomalies.length} open anomal{clientAnomalies.length === 1 ? "y" : "ies"} — resolve by coding the transactions above
                      </p>
                      {clientAnomalies.map((a: { id: number; description: string; severity: string; detectedAt?: string | null }) => (
                        <div key={a.id} className="rounded-lg p-4 flex items-start gap-3"
                          style={{ background: a.severity === "high" ? S.redBg : S.amberBg, border: `1px solid ${a.severity === "high" ? "oklch(0.75 0.16 25)" : "oklch(0.80 0.12 85)"}` }}>
                          <TrendingDown className="w-4 h-4 shrink-0 mt-0.5" style={{ color: a.severity === "high" ? S.red : S.amber }} />
                          <div>
                            <p className="text-sm font-semibold" style={{ color: a.severity === "high" ? S.red : S.amber }}>
                              {a.severity.toUpperCase()}
                            </p>
                            <p className="text-xs mt-0.5" style={{ color: S.text2 }}>{a.description}</p>
                            {a.detectedAt && <p className="text-[10px] mt-1" style={{ color: S.textMuted }}>Detected {new Date(a.detectedAt).toLocaleDateString()}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
